import React, { useRef, useCallback, useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import * as serializer from 'basic-editor-value-serializer';

import application from 'application';

import BasicTextEditor from 'generics/components/basic-text-editor-slate';
import Helpers from 'generics/components/basic-text-editor-slate/helpers';
import HelptapEditor from 'generics/components/basic-text-editor-slate/helpers';

import {
  getChatMessageEditorEnabledElements
} from 'root/startup-view/customers/customer-message-editor/utilities';

import MessageEditorSecondaryControls from './message-editor-secondary-controls/react';

import internalEventCodes from 'event-system/codes/internal';
import externalEventCodes from 'event-system/codes/external';

import SenderButton from './sender-button/react';

import './index.scss';

function MessageEditor(props) {

  const editorReference = useRef(null);

  const [rootState, setRootState] = useState({
    enableSend: true,
    outboundDisabled: props.conversation.outboundDisabled,
    senderToggled: false
  });

  const handleContentChange = useCallback(function handleContentChange(editorState) {
    setEnableSend(Helpers.isEmpty(editorReference.current) === false);
    props.onChange(editorState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function registerEvents() {

    const eventRegistrers = [
      registerInboundMessageUpdatedHanlder,
      registerOutboundMessageUpdatedHanlder,
      registerConversationUpdatedHanlder
    ];
    const eventUnregisters = [];

    for (let eventRegistrer of eventRegistrers) {
      eventUnregisters.push(eventRegistrer());
    }

    return function unregisterEventListeners() {

      for (let eventUnregister of eventUnregisters) {
        eventUnregister();
      }

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function registerInboundMessageUpdatedHanlder() {

    function handleUpdatedConversation(payload) {

      trySetOutboundDisabled(payload.conversation._id, payload.conversation.outboundDisabled);

    }

    application.eventSystem.internal.on('INBOUND_CONVERSATION_MESSAGE_UPDATED', handleUpdatedConversation);

    return function unregisterInboundMessageUpdatedHanlder() {
      application.eventSystem.internal.off('INBOUND_CONVERSATION_MESSAGE_UPDATED', handleUpdatedConversation);
    };

  }

  function registerOutboundMessageUpdatedHanlder() {

    function handleUpdatedConversation(payload) {

      trySetOutboundDisabled(payload.conversation._id, payload.conversation.outboundDisabled);

    }

    application.eventSystem.internal.on(externalEventCodes.OUTBOUND_CONVERSATION_MESSAGE_CREATED, handleUpdatedConversation);

    return function unregisterInboundMessageUpdatedHanlder() {
      application.eventSystem.internal.off(externalEventCodes.OUTBOUND_CONVERSATION_MESSAGE_CREATED, handleUpdatedConversation);
    };

  }

  function registerConversationUpdatedHanlder() {

    function handleUpdatedConversation(payload) {

      trySetOutboundDisabled(payload.conversation._id, payload.conversation.outboundDisabled);

    }

    application.eventSystem.internal.on('CONVERSATION_UPDATED', handleUpdatedConversation);

    return function unregisterInboundMessageUpdatedHanlder() {
      application.eventSystem.internal.off('CONVERSATION_UPDATED', handleUpdatedConversation);
    };

  }

  function trySetOutboundDisabled(conversationId, outboundDisabled) {
    setRootState(function updateRootState(rootState) {

      if (props.conversation._id !== conversationId) {
        return rootState;
      }

      return {
        ...rootState,
        outboundDisabled
      };

    });

  }

  function setEnableSend(enableSend) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        enableSend
      };

    });

  }

  function handleEditorReady(editor) {

    editorReference.current = editor;

    const onInputReady = props.onInputReady;

    if (typeof onInputReady !== 'function') {
      return;
    }

    onInputReady(editor);

  }

  function getDefaultEditorContent() {

    return [{
      padding: [0, 0, 0, 0],
      margin: [0, 0, 0, 0],
      type: 'paragraph',
      children: [{
        text: ''
      }]
    }];

  }

  function sendMessage() {
    const editor = editorReference.current;
    const editorState = editor.children;
    props.onComplete(editor.children, {
      html: serializer.toNativeHTML(editorState),
      text: serializer.toText(editorState)
    });
    HelptapEditor.setContent(editorReference.current, getDefaultEditorContent());
  }

  function renderEditor() {
    const contentFieldAttributes = {
      placeholder: 'Write your message...',
      value: props.content,
      onChange: handleContentChange,
      onEnterKey(event, editor) {
        if (event.metaKey === false) {
          return false;
        }
        if (rootState.enableSend === false) {
          application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
            message: 'Please wait until files are uploaded',
            seconds: 5,
            type: 'error'
          });
          return false;
        }
        if (rootState.outboundDisabled === true) {
          return;
        }
        event.preventDefault();
        sendMessage();
        return true;
      },
      mediaUploadControls: {
        layout: 'columns',
        position: 'right bottom'
      },
      mediaUploader(file) {
        return props.mediaUploader(file);
      },
      onEditorReady(editor) {
        handleEditorReady(editor);

        ReactEditor.focus(editor);
        const endPoint = Editor.end(editor, []);
        Transforms.select(editor, endPoint);

        // HelptapEditor.setContent(editor, props.content);
      },
      overlayMenu: true,
      focus: true,
      adjustInputPosition: true,
      enabledElements: getChatMessageEditorEnabledElements(),
      mentionTypes: props.mentionTypes,
      readOnly: props.readOnly,
      onFocus: props.onFocus,
      onBlur: props.onBlur,
      reserveEnterKey: false,
      onKeyDown: props.onKeyDown,
      onChangeDelay: 1,
      direction: application.domUtility.determineLangaugeDetails().dir
    };

    return <div id="message-editor-element-container">
      <BasicTextEditor {...contentFieldAttributes} />
    </div>;

  }

  function renderAuthor() {

    const conversation = props.conversation;
    const commentAuthorPictureAttributes = {
      conversation,
      draftId: props.draftId,
      onChange: props.onSenderChange
    };

    return <SenderButton {...commentAuthorPictureAttributes} />;

  }

  function tryRenderSecondaryControls() {

    if (props.attachments === null) {
      return;
    }

    const conversation = props.conversation;
    const secondaryControlsProperties = {
      enableSend: rootState.enableSend,
      outboundDisabled: rootState.outboundDisabled,
      internetPhone: props.internetPhone,
      conversation,
      attachments: props.attachments,
      createAttachment: props.createAttachment,
      deleteAttachment: props.deleteAttachment,
      onImage(file) {
        const editor = editorReference.current;
        Helpers.insertHTFile(editor, '', void 0, file);
        Helpers.insertParagraph(editor, {});
        Helpers.focus(editor);
        setTimeout(() => Helpers.scrollToFocusedNode(editor), 10);
      },
      onWorking() { setEnableSend(false); },
      onIdle() { setEnableSend(true); },
      disableAILevel: props.disableAILevel,
      enableBookmarks: props.enableBookmarks,
      bookmarks: props.bookmarks,
      sendMessage,
      displaySendButton: true
    };

    return <MessageEditorSecondaryControls {...secondaryControlsProperties} />;

  }

  function renderMain() {

    return <div id="inbox-message-editor-main">
      <div id="inbox-message-editor-wrapper">
        {renderAuthor()}
        {renderEditor()}
        {tryRenderSecondaryControls()}
      </div>
    </div>;

  }

  return renderMain();

}

export function render(properties) {

  return <MessageEditor {...properties} />;

}

export function renderV2(mountPoint, properties) {

  let root = ReactDOM.createRoot(mountPoint);
  let editorRef = { current: null };
  let mentionTypes = properties.mentionTypes;

  function buildProperties(props) {
    const originalOnInputReady = props.onInputReady;
    return {
      ...props,
      mentionTypes,
      onInputReady: function onInputReady(editor) {
        editorRef.current = editor;
        if (typeof originalOnInputReady === 'function') {
          originalOnInputReady(editor);
        }
      }
    };
  }

  root.render(<MessageEditor {...buildProperties(properties)} />);

  return {
    update(properties) {

      if (root === null) {
        return;
      }

      root.render(<MessageEditor {...buildProperties(properties)} />);
    },
    unmount() {

      root.unmount();

      root = null;
    },
    remount(properties) {

      if (root) {
        try {
          root.unmount();
        } catch (exception) {}
      }

      root = ReactDOM.createRoot(mountPoint);

      root.render(<MessageEditor {...buildProperties(properties)} />);
    },
    focus() {

      if (editorRef.current === null) {
        return;
      }

      ReactEditor.focus(editorRef.current);
    }
  };

}

export default MessageEditor;
